import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase, TABLES, SafeIcon } from '../../lib/core';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';

const { FiPackage, FiPlus, FiCamera, FiCheck, FiAlertCircle, FiX, FiActivity, FiSearch, FiShield } = FiIcons;

const LogDrops = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [selectedChar, setSelectedChar] = useState('');
  const [selectedItem, setSelectedItem] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [recentLogs, setRecentLogs] = useState([]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [notification, setNotification] = useState(null);
  const [masterSettings, setMasterSettings] = useState({}); // master_id -> { is_base_pay_only }

  useEffect(() => {
    if (user?.id) {
      fetchMasterSettings();
      fetchCharacters();
      fetchRecentLogs();
    }
  }, [user]);

  useEffect(() => {
    if (selectedChar) fetchItemsForCharacter();
    else setItems([]);
  }, [selectedChar]);

  const notify = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const fetchMasterSettings = async () => {
    const { data } = await supabase
      .from(TABLES.PILOT_MASTERS)
      .select('master_id, is_base_pay_only')
      .eq('pilot_id', user.id);
    
    const settings = {};
    data?.forEach(r => { settings[r.master_id] = r.is_base_pay_only; });
    setMasterSettings(settings);
  };

  const fetchCharacters = async () => {
    const { data: pmData } = await supabase.from(TABLES.PILOT_MASTERS).select('master_id').eq('pilot_id', user.id).eq('status', 'active');
    const masterIds = (pmData || []).map(m => m.master_id);
    if (masterIds.length === 0) { setCharacters([]); return; }
    const { data } = await supabase.from(TABLES.CHARACTERS).select('*, master:master_id(id, nickname, username)').eq('pilot_id', user.id).in('master_id', masterIds);
    setCharacters(data || []);
  };

  const fetchItemsForCharacter = async () => {
    const char = characters.find(c => c.id === selectedChar);
    if (!char) return;
    const { data } = await supabase.from(TABLES.ITEMS).select('*').or(`master_id.eq.${char.master_id},master_id.is.null`).order('name', { ascending: true });
    setItems(data || []);
  };

  const fetchRecentLogs = async () => {
    const { data } = await supabase.from(TABLES.LOGS).select(`*, item:${TABLES.ITEMS}(name), char:${TABLES.CHARACTERS}(name)`).eq('pilot_id', user.id).order('created_at', { ascending: false }).limit(10);
    setRecentLogs(data || []);
  };

  const filteredItems = items.filter(i => i.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const currentChar = characters.find(c => c.id === selectedChar);
  const isFixedSalary = currentChar && masterSettings[currentChar.master_id];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedItem || !selectedChar) return;
    const item = items.find(i => i.id === selectedItem);
    setLoading(true);
    let proofUrl = null;
    try {
      if (file) {
        const fileName = `${Date.now()}-${user.id}.${file.name.split('.').pop()}`;
        const filePath = `drop-proofs/${user.id}/${fileName}`;
        const { error: uploadError } = await supabase.storage.from('payout-proofs').upload(filePath, file);
        if (!uploadError) {
          const { data: { publicUrl } } = supabase.storage.from('payout-proofs').getPublicUrl(filePath);
          proofUrl = publicUrl;
        }
      }
      const qty = parseInt(quantity) || 1;
      const { error } = await supabase.from(TABLES.LOGS).insert([{
        pilot_id: user.id,
        master_id: currentChar.master_id,
        item_id: selectedItem,
        character_id: selectedChar,
        quantity: qty,
        value_at_log: item.rate * qty,
        status: 'pending_master',
        proof_url: proofUrl
      }]);
      if (error) throw error;
      notify(`Loot logged for Master ${currentChar.master.nickname || currentChar.master.username}!`);
      setSelectedItem('');
      setSearchTerm('');
      setFile(null);
      setPreview(null);
      fetchRecentLogs();
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 pb-20 relative">
      <AnimatePresence>
        {notification && (
          <motion.div initial={{ opacity: 0, y: -50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9 }} className={`fixed top-6 right-6 z-[100] px-6 py-4 rounded-2xl shadow-2xl border flex items-center gap-3 backdrop-blur-md ${notification.type === 'success' ? 'bg-emerald-500/90 text-white border-emerald-400' : 'bg-red-500/90 text-white border-red-400'}`}>
            <SafeIcon icon={notification.type === 'success' ? FiCheck : FiAlertCircle} />
            <span className="font-black text-[10px] uppercase tracking-widest">{notification.message}</span>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="space-y-8">
        <div className="bg-white p-6 md:p-10 rounded-[2.5rem] border border-gray-100 shadow-sm shadow-gray-200/50">
          <h2 className="text-3xl font-black text-gray-900 mb-8 flex items-center gap-3">
            <FiPackage className="text-indigo-600" /> Log Loot
          </h2>
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="space-y-4">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Select Active Character</label>
              <div className="grid grid-cols-1 gap-3">
                {characters.length === 0 ? (
                  <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest p-8 border-2 border-dashed border-gray-100 rounded-2xl text-center italic">No active Master teams found</div>
                ) : (
                  characters.map(char => (
                    <button key={char.id} type="button" onClick={() => setSelectedChar(char.id)} className={`p-5 rounded-2xl border-2 text-left transition-all ${selectedChar === char.id ? 'border-indigo-600 bg-indigo-50 shadow-lg shadow-indigo-100/50' : 'border-gray-100 hover:border-gray-200 bg-gray-50/50'}`}>
                      <div className="flex justify-between items-center">
                        <div>
                          <div className={`font-black text-lg ${selectedChar === char.id ? 'text-indigo-600' : 'text-gray-900'}`}>{char.name}</div>
                          <div className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mt-1">Master: {char.master?.nickname || char.master?.username}</div>
                        </div>
                        {masterSettings[char.master_id] && (
                          <div className="px-3 py-1 bg-amber-100 text-amber-600 rounded-full text-[8px] font-black uppercase tracking-widest flex items-center gap-1">
                            <FiShield /> Fixed Salary
                          </div>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            {selectedChar && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
                <div className="space-y-4">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Search & Select Item</label>
                  <div className="relative">
                    <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input className="apple-input !pl-12 !py-4 bg-gray-50 mb-3" placeholder="Filter items..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                  </div>
                  <select className="apple-input cursor-pointer font-black text-gray-700" value={selectedItem} onChange={e => setSelectedItem(e.target.value)} required>
                    <option value="">Choose item from catalog...</option>
                    {filteredItems.map(i => (
                      <option key={i.id} value={i.id}>
                        {i.name} {isFixedSalary ? '(Price Restricted)' : `(₱${i.rate.toLocaleString()})`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Quantity</label>
                    <input type="number" className="apple-input font-black text-xl text-center" value={quantity} onChange={e => setQuantity(e.target.value)} min="1" required />
                  </div>
                  <div className="space-y-4">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Proof Screenshot</label>
                    <div className="relative">
                      <input type="file" id="drop-proof" accept="image/*" onChange={(e) => { const f = e.target.files[0]; if (f) { setFile(f); setPreview(URL.createObjectURL(f)); } }} className="hidden" />
                      <label htmlFor="drop-proof" className={`flex items-center justify-center h-[60px] cursor-pointer rounded-2xl border-2 border-dashed transition-all ${preview ? 'border-emerald-500 bg-emerald-50 text-emerald-600' : 'border-gray-200 hover:border-indigo-400 text-gray-400'}`}>
                        <FiCamera className="mr-2 text-lg" />
                        <span className="text-[10px] font-black uppercase tracking-widest">{preview ? 'Proof Ready' : 'Upload Screenshot'}</span>
                      </label>
                    </div>
                  </div>
                </div>
                <button type="submit" disabled={loading || !selectedItem} className="apple-button-primary w-full !py-5 text-lg font-black uppercase tracking-widest shadow-2xl shadow-blue-500/20">
                  {loading ? <div className="w-6 h-6 border-4 border-white/30 border-t-white rounded-full animate-spin" /> : 'Confirm Drop Entry'}
                </button>
              </motion.div>
            )}
          </form>
        </div>
      </div>
      <div className="bg-white p-6 md:p-10 rounded-[2.5rem] border border-gray-100 shadow-sm flex flex-col max-h-[800px]">
        <h2 className="text-3xl font-black text-gray-900 mb-8 flex items-center gap-3">
          <FiActivity className="text-blue-500" /> Recent Entries
        </h2>
        <div className="flex-1 space-y-6 overflow-y-auto no-scrollbar pr-2">
          {recentLogs.map(log => {
            const isLogFixed = masterSettings[log.master_id];
            return (
              <div key={log.id} className="p-6 bg-gray-50 rounded-3xl border border-transparent hover:border-indigo-100 hover:bg-white transition-all group shadow-sm">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center text-indigo-600 shadow-sm overflow-hidden border border-gray-100">
                      {log.proof_url ? <img src={log.proof_url} className="w-full h-full object-cover group-hover:scale-110 transition-all duration-500" /> : <FiPackage className="text-xl" />}
                    </div>
                    <div>
                      <h4 className="font-black text-gray-900 text-lg leading-tight">{log.item?.name} <span className="text-gray-300">x{log.quantity}</span></h4>
                      <p className="text-[9px] font-black text-indigo-500 uppercase tracking-widest mt-1 italic">{log.char?.name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    {isLogFixed ? (
                      <p className="text-xs font-black text-amber-600 tracking-widest uppercase">Fixed Rate</p>
                    ) : (
                      <p className="text-xl font-black text-emerald-600 tracking-tighter">₱{log.value_at_log?.toLocaleString()}</p>
                    )}
                    <p className={`text-[8px] font-black uppercase tracking-widest mt-1 px-2 py-0.5 rounded-full inline-block ${log.status === 'paid' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>{log.status}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LogDrops;